import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  useDeleteDocument,
  useDocumentDownloadUrl,
  useListDocuments,
  useUpdateDocumentTags,
  useUploadDocument,
} from '@/hooks/useDocuments';
import { Search, Upload as UploadIcon } from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';

interface AssetDocumentsProps {
  assetId: number;
}

export const AssetDocuments: React.FC<AssetDocumentsProps> = ({ assetId }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState<string>('');
  const [addTagInput, setAddTagInput] = useState<{ [docId: number]: string }>(
    {},
  );
  const [optimisticTags, setOptimisticTags] = useState<{
    [docId: number]: string[];
  }>({});
  const {
    data: documents,
    isLoading,
    error,
  } = useListDocuments({ asset_id: assetId, search });
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();
  const updateTagsMutation = useUpdateDocumentTags();
  const [viewDoc, setViewDoc] = useState<any | null>(null);
  const { data: viewUrl, isLoading: viewLoading } = useDocumentDownloadUrl(
    viewDoc?.id,
    { enabled: !!viewDoc },
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_id', String(assetId));
    uploadMutation.mutate(formData);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this document?')) {
      deleteMutation.mutate(id);
    }
  };

  // Inline tag add
  const handleAddTag = (doc: any) => {
    const newTag = (addTagInput[doc.id] || '').trim();
    if (!newTag || (doc.tags || []).includes(newTag)) return;
    const newTags = [...(doc.tags || []), newTag];
    setOptimisticTags((prev) => ({ ...prev, [doc.id]: newTags }));
    updateTagsMutation.mutate(
      { id: doc.id, tags: newTags },
      {
        onSettled: () => {
          setOptimisticTags((prev) => {
            const { [doc.id]: _, ...rest } = prev;
            return rest;
          });
        },
      },
    );
    setAddTagInput((prev) => ({ ...prev, [doc.id]: '' }));
  };

  // Inline tag remove
  const handleRemoveTag = (doc: any, tag: string) => {
    const newTags = (doc.tags || []).filter((t: string) => t !== tag);
    setOptimisticTags((prev) => ({ ...prev, [doc.id]: newTags }));
    updateTagsMutation.mutate(
      { id: doc.id, tags: newTags },
      {
        onSettled: () => {
          setOptimisticTags((prev) => {
            const { [doc.id]: _, ...rest } = prev;
            return rest;
          });
        },
      },
    );
  };

  // Editable tag pills
  const renderEditableTags = (doc: any) => {
    const tags = optimisticTags[doc.id] ?? doc.tags;
    if (!tags || tags.length === 0) return null;
    return (
      <span className="flex flex-wrap gap-1">
        {tags.map((tag: string, idx: number) => (
          <span
            key={idx}
            className="bg-blue-100 text-xs px-2 py-0.5 rounded-full text-blue-700 flex items-center gap-1"
          >
            <span className="mr-1">{tag}</span>
            <button
              type="button"
              className="text-blue-500 hover:text-red-500 focus:outline-none"
              onClick={() => handleRemoveTag(doc, tag)}
              aria-label={`Remove tag ${tag}`}
              disabled={updateTagsMutation.isPending}
            >
              ×
            </button>
          </span>
        ))}
      </span>
    );
  };

  const handleView = (doc: any) => {
    setViewDoc(doc);
  };
  const handleCloseView = () => {
    setViewDoc(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Documents</CardTitle>
        <div className="flex items-center gap-2">
          <div className="relative w-60">
            <Input
              type="text"
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              style={{ minWidth: 220 }}
            />
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            id="asset-doc-upload"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            size="sm"
            variant="outline"
          >
            <UploadIcon className="h-4 w-4 mr-1" />
            Upload Document
          </Button>
          {uploadMutation.isPending && <span>Uploading...</span>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <div>Loading documents...</div>}
        {error && <div className="text-red-500">Error loading documents</div>}
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="w-[40%] text-left">Name</th>
                <th className="w-[30%] text-left">Tags</th>
                <th className="w-[30%] text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents && documents.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="text-center text-muted-foreground py-4"
                  >
                    No documents found.
                  </td>
                </tr>
              )}
              {documents?.map((doc: any) => (
                <tr key={doc.id} className="border-b last:border-0">
                  <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                    {doc.original_name}
                  </td>
                  <td className="py-2 pr-4">
                    {renderEditableTags(doc)}
                    <div className="flex items-center gap-1 mt-1">
                      <input
                        type="text"
                        placeholder="Add tag..."
                        value={addTagInput[doc.id] || ''}
                        onChange={(e) =>
                          setAddTagInput((prev) => ({
                            ...prev,
                            [doc.id]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddTag(doc);
                        }}
                        className="border rounded px-1 py-0.5 text-xs"
                        style={{ width: 80 }}
                        disabled={updateTagsMutation.isPending}
                      />
                      <Button
                        size="xs"
                        variant="outline"
                        className="px-2 py-0.5"
                        onClick={() => handleAddTag(doc)}
                        disabled={
                          updateTagsMutation.isPending ||
                          !(addTagInput[doc.id] || '').trim()
                        }
                      >
                        {updateTagsMutation.isPending ? (
                          <span className="animate-spin">⏳</span>
                        ) : (
                          '+'
                        )}
                      </Button>
                    </div>
                  </td>
                  <td className="py-2 pr-4 flex gap-2 items-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(doc)}
                    >
                      View
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(doc.id)}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* View Document Modal */}
        <Dialog open={!!viewDoc} onOpenChange={handleCloseView}>
          <DialogContent className="max-w-4xl w-full h-[80vh]">
            <DialogHeader>
              <DialogTitle>
                {viewDoc?.original_name || 'View Document'}
              </DialogTitle>
            </DialogHeader>
            {viewLoading ? (
              <div className="flex justify-center items-center h-64">
                Loading...
              </div>
            ) : viewUrl ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                {viewDoc?.original_name?.match(/\.(pdf)$/i) ? (
                  <iframe
                    src={viewUrl}
                    title="PDF Preview"
                    className="w-full h-[70vh] border rounded"
                  />
                ) : viewDoc?.original_name?.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i,
                  ) ? (
                  <img
                    src={viewUrl}
                    alt={viewDoc.original_name}
                    className="max-h-[70vh] max-w-full object-contain border rounded"
                  />
                ) : (
                  <a
                    href={viewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Download/Open Document
                  </a>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground">
                No preview available.
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AssetDocuments;
